import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WellRequestLog } from 'src/app/model/well-request-log.model';
import { BackendService } from 'src/app/services/backend.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { WellRequestStatus_Options } from '../../model/status-enum.model';
import { RoutingService } from '../../services/routing.service';
import { StatusHelperService } from '../../services/status-helper.service';
import { WellDetailsComponent } from '../well-details/well-details.component';
import { EmailService } from '../../services/email.service';
import { MsalService } from '@azure/msal-angular';
import { IAdmin } from '../../model/admin.model';
import { Subscription, lastValueFrom } from 'rxjs';
import { ApproveResponse } from '../../model/approve-response.model';
import 'src/app/extensions/admin-array-extension';

@Component({
    selector: 'app-well-admin-details',
    templateUrl: './well-admin-details.component.html',
    styleUrls: ['./well-admin-details.component.css'],
    providers: [RoutingService],
    standalone: false
})
export class WellAdminsDetailsComponent extends WellDetailsComponent implements OnInit, OnDestroy {
  private _currentStatus: WellRequestStatus_Options | undefined;
  private _isStatusChanging: boolean = false;
  private _statusChangesSubscription: Subscription | undefined;
  _isSaveDisable: boolean = true;

  admins: IAdmin[] = [];
  override filteredAdmins: IAdmin[] = [];
  override selectedAdmins: IAdmin[] = [];
  override initialAdmins: IAdmin[] = [];

  constructor(
    _backendService: BackendService,
    _permissionService: PermissionsService,
    _routingSevice: RoutingService,
    _snackBar: MatSnackBar,
     _msalService: MsalService,
    _formBuilder: FormBuilder,
    _email: EmailService,
    private readonly _statusHelper: StatusHelperService) {
    super(_routingSevice, _snackBar, _formBuilder, _msalService ,_backendService, _permissionService, _email);
    this.form = this.fb.group({
      comments: ['', Validators.compose([Validators.maxLength(250)])],
      admin: [{value: '', disabled: false}, Validators.required]
    });

    this._statusChangesSubscription = this.statusChanges();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadAdmins();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this._statusChangesSubscription?.unsubscribe();
  }

  private async loadAdmins(): Promise<void> {
    this.admins = await lastValueFrom(this.backendService.getAdmins());
    
    this.wellRequest$.subscribe((wellRequest) => {
      if (wellRequest?.id) {
        this.loadWellRequestAdmins(wellRequest.id.toString());
      }
    });
  }

  private loadWellRequestAdmins(wellRequestId: string): void {
    this.backendService.GetWellRequestAdmins(wellRequestId).subscribe((admins: any[]) => {
      const adminIds = admins.map(a => a.id);
      this.selectedAdmins = this.admins.filter(admin => adminIds.includes(admin.id));
      this.initialAdmins = this.selectedAdmins.slice();
      this.filteredAdmins = this.admins;
      this.form.controls['admin'].patchValue(this.selectedAdmins, { emitEvent: false });
    });
  }

  isAdminRemovable = (event: any) => {
    let admin = event as IAdmin;
    return !admin.isTookAction;
  }

  onAdminSelected(admins: any): void {
    this.selectedAdmins = admins as IAdmin[];
    this.saveAdminChanges();
  }

  onAdminRemoved(event: any): void {
    this.selectedAdmins = event.afterRemove as IAdmin[];
    this.saveAdminChanges();
  }

  private saveAdminChanges(): void {
    if (!this.wellRequest?.id || !this.wellRequest?.groups) {
      return;
    }

    if (this.selectedAdmins.equalAdmins(this.initialAdmins)) {
      return;
    }

    const adminIds = this.selectedAdmins
      .getWellRequestAdmins(this.wellRequest.groups)
      .map(ag => ag.adminId) as string[];

    this.backendService.updateAdmins(adminIds, this.wellRequest.id.toString())
      .subscribe({
        next: () => {
          this.snackBar.open('Admins updated successfully', 'Close ✕', {
            duration: 3000,
            verticalPosition: 'bottom',
            horizontalPosition: 'end'
          });

          const newAdmins = this.selectedAdmins.exceptAdmins(this.initialAdmins);
          if (newAdmins.length > 0 && this.wellRequest?.id) {
            this.email.EmailNotify(BigInt(this.wellRequest.id), true, newAdmins);
          }

          this.initialAdmins = this.selectedAdmins.slice();
        },
        error: (error) => {
          console.error('Error updating admins:', error);
          this.snackBar.open('Failed to update admins', 'Close ✕', {
            duration: 5000,
            verticalPosition: 'bottom',
            horizontalPosition: 'end',
            panelClass: 'error-snackbar'
          });
        }
      });
  }

  saveChangesSubmit(): void {
    if (this._currentStatus && this.wellRequest?.id) {
      this.createPendingRequest(this._currentStatus, this.wellRequest.id)
        .subscribe({
          next: () => {
            this.redirect();
          },
          error: (error) => {
            console.error('Error creating pending request:', error);
            this.snackBar.open('Failed to update status. Please try again.', 'Close ✕', {
              duration: 5000,
              verticalPosition: 'bottom',
              horizontalPosition: 'end'
            });
          }
        });
    }
  }

  private statusChanges(): Subscription {
    return this._statusHelper.statusItem$.subscribe((data) => {
      this._currentStatus = data;

      if (!this.wellRequest || data == this.wellRequest?.currentStatusId) {
        this._isSaveDisable = true;
        return;
      }

      if (this._isStatusChanging) {
        return;
      }

      this._isSaveDisable = false;

      if (this.wellRequest?.id) {
        this._isStatusChanging = true;
        
        this.createPendingRequest(data, this.wellRequest.id)
          .subscribe({
            next: (response: ApproveResponse) => {
              this._isStatusChanging = false;
              this.snackBar.open(response.message, 'Close ✕', {
                duration: 3000,
                verticalPosition: 'bottom',
                horizontalPosition: 'end'
              });
            },
            error: (error) => {
              this._isStatusChanging = false;
              console.error('Error creating pending request:', error);
              this.snackBar.open('Failed to update status. Please try again.', 'Close ✕', {
                duration: 5000,
                verticalPosition: 'bottom',
                horizontalPosition: 'end'
              });
            }
          });
      }
    });
  }
}
